<script lang="ts" setup>
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useDescriptor from '~/composables/useDescriptor'
import { useContentHelper } from '~/composables/useContentHelper'
import useColors from '~/composables/colors'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'
import SharedCardBadge from '~/components/cards/badges/SharedCardBadge.vue'

const props = withDefaults(defineProps<{
  resource: BaseItem
  loading?: 'eager' | 'lazy'
  task?: Task
}>(), {
  loading: 'lazy'
})

const { getTitle } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()
const { t } = useI18n()

const elementRef = ref()

const { backgroundStyle } = useColors({
  identifier: props.resource.locationId!,
  elementRef,
})

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const label = getDescriptor(props.resource)
const labelClasses = 'rounded-md bg-green-60 px-2 py-1 text-xs font-medium text-white'
</script>

<template>
  <article
    ref="elementRef"
    :style="backgroundStyle"
    class="group animate-fade relative flex h-[17.5rem] flex-col justify-between space-y-2 rounded-xl p-4 hover:shadow-lg has-focus-visible:ring"
  >
    <SharedCardBadge v-if="task?.homework" />
    <aside
      v-else-if="resource.new"
      class="bg-green-40 pointer-events-none absolute top-0 right-0 z-20 rounded-bl-2xl p-1.5 pl-2 text-sm font-medium tracking-wider text-white uppercase"
      v-text="t('newShort')"
    />

    <div class="flex gap-1">
      <span
        :class="labelClasses"
        v-text="label"
      />
      <span
        v-if="resource.tiered"
        :class="labelClasses"
        v-text="t('search.checkboxes.tiered')"
      />
    </div>
    <h3 class="text-xl font-medium text-black">
      <LinkOrAnchor
        :link="link"
        class="line-clamp-3 outline-0 before:absolute before:inset-0 before:rounded-2xl before:transition before:hover:shadow-lg"
      >
        {{ title }}
      </LinkOrAnchor>
    </h3>
    <div
      v-if="resource.thumbnail"
      class="pointer-events-none aspect-video overflow-hidden rounded-xl"
    >
      <img
        :alt="resource.thumbnailAlt || ''"
        :loading
        :src="resource.thumbnail"
        class="size-full rounded-xl object-cover transition duration-200 group-hover:scale-110"
      >
    </div>
    <CardMenu
      :resource="resource"
      toggle-shade="dark"
      button-classes="z-10 mb-0"
    />
  </article>
</template>
